/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { useLocalization, WalletPassCard } from "@lemmonhq/uikit";
import type { WalletBindingValues } from "@lemmonhq/blogic/wallet-design";
import type { CollectedProgram } from "@app/server/client";
import type { WalletSelectedDesign } from "@lemmonhq/types";
import { coinIconOf } from "../../components";
import { useCustomer } from "../../providers/auth-context";
import { CARD_PANEL_HEIGHT } from "./cardChrome";
import { coinCardBindings, passBrowserView } from "./passDesign";

/**
 * A loyalty card, in the sense a wallet app means it: a portrait pass drawn
 * from the program's wallet pass design — the same design Apple and Google
 * Wallet render — with the live stamp band and progress laid over it.
 *
 * One card per *program*, not per shop: a coffee card and a lunch card are two
 * things you would hold in your hand, with two balances and two rewards.
 */

/** The next reward worth working towards: the cheapest tier not yet affordable. */
export const nextTierOf = (program: CollectedProgram) => {
  const tiers = program.tiers ?? [];
  if (tiers.length === 0) return null;
  return tiers.find((tier) => tier.amount > program.balance) ?? tiers[0];
};

export type WalletCardProps = {
  program: CollectedProgram;
  shopName: string;
  /** Membership passes are shaped into a program; they carry their own design. */
  kind?: "coin" | "membership";
  /** Overrides `program.design` (membership passes). */
  design?: WalletSelectedDesign | null;
  /** Binding values beyond what the coin program provides. */
  bindings?: WalletBindingValues;
  /**
   * What fills the middle of the pass. Defaults to the stamp row; the sheets
   * pass the till-sized QR, so the scannable card is the same object as the
   * card in the wallet rather than a second thing that resembles it.
   */
  center?: () => any;
  /** Drop the width cap and fill the container — the stack owns its own width. */
  wide?: boolean;
  /** Overrides the design's barcode for the small code on the panel edge. */
  qrValue?: string | null;
  onSelect?: () => void;
  /** Glyph for the header and stamps; coin programs map theirs via coinIconOf. */
  iconOverride?: string;
  /** Scanner/back face: reserve the panel for the complete reward text. */
  back?: boolean;
  /** Temporary balance used while a just-claimed receipt counts stamps in. */
  displayBalance?: number;
};

export const WalletCard = defineComponent<WalletCardProps>({
  name: "WalletCard",
  props: [
    "program",
    "shopName",
    "kind",
    "design",
    "bindings",
    "center",
    "wide",
    "qrValue",
    "onSelect",
    "iconOverride",
    "back",
    "displayBalance",
  ],
  setup(props) {
    const { t, locale } = useLocalization();
    const { customer } = useCustomer();

    const kind = computed(() => props.kind ?? "coin");
    const tier = computed(() => nextTierOf(props.program));
    const balance = computed(
      () => props.displayBalance ?? props.program.balance,
    );

    const view = computed(() =>
      passBrowserView(
        kind.value,
        props.design !== undefined ? props.design : props.program.design,
        {
          ...(kind.value === "coin"
            ? coinCardBindings(props.program, props.shopName, tier.value, {
                balance: balance.value,
                t,
              })
            : {}),
          ...(customer.value?.id
            ? {
                "loyalty.qr": `lemmon-loyalty:${customer.value.id}`,
                "customer.short_id": customer.value.id
                  .slice(0, 8)
                  .toUpperCase(),
              }
            : {}),
          ...(customer.value && "name" in customer.value && customer.value.name
            ? { "customer.name": customer.value.name }
            : {}),
          ...props.bindings,
        },
        locale.value || "en",
      ),
    );

    return () => {
      // Every program has a design; one that fails validation is shown as
      // unavailable rather than replaced with an unrelated card.
      if (!view.value) {
        return (
          <div
            class={[
              "rounded-3xl bg-base-200 text-base-content/60 flex items-center justify-center p-6 text-center text-sm",
              props.wide
                ? "w-full h-full"
                : "aspect-[5/7] w-full max-w-[20rem] mx-auto",
            ]}
          >
            {t("customer.collected.card_unavailable")}
          </div>
        );
      }
      const target = tier.value?.amount ?? 0;
      const reached = !!tier.value && balance.value >= target;
      return (
        <WalletPassCard
          view={view.value}
          icon={props.iconOverride ?? coinIconOf(props.program.icon)}
          fallbackTitle={props.shopName}
          stampCount={kind.value === "coin" ? balance.value : null}
          stampTarget={target || null}
          animateStamps={props.displayBalance !== undefined}
          progress={
            tier.value
              ? {
                  value: balance.value,
                  max: target,
                  label: reached
                    ? t("customer.collected.reward_ready")
                    : t("customer.collected.to_go", [target - balance.value]),
                }
              : null
          }
          center={props.center}
          qrValue={props.qrValue}
          wide={props.wide}
          panelClass={CARD_PANEL_HEIGHT}
          back={props.back}
          onSelect={props.onSelect}
        />
      );
    };
  },
});
